import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Modal from '@material-ui/core/Modal';
import Drawer from '@material-ui/core/Drawer';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Header from '../../components/Header';
import MenuDrawer from '../../components/MenuDrawer';
import SearchButton from '../../components/atoms/SearchButton';
import MenuRegisterButton from '../../components/atoms/MenuRegisterButton';
import GochiEventSettingTable from '../organisms/GochiEventSettingTable';
import GochiEventSettingAdd from '../organisms/GochiEventSettingAdd';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import { isAuthenticated, sendLogin } from '../../auth/Auth';
import { getGochiEventSetting } from '../../actions/GochiEventSetting';
import { getAccounts } from '../../actions/Accounts';

const styles = theme => ({
  root: {
    backgroundColor: '#e0e0e0',
    flexGrow: 1,
    width: '100%'
  },
  pageTitileDiv: {
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  pageDiscriptionDiv: {
    paddingLeft: 26,
    paddingTop: 2,
    textAlign: 'left'
  },
  mainPaper: {
    position: 'relative',
    margin: 20
  },
  zoom: {
    position: 'relative',
    left: -10,
    top: 20
  },
  mainPaperTitleDiv: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '20px 26px 10px 26px'
  },
  searchForm: {
    padding: '10px 26px 10px 26px'
  },
  textField: {
    padding: '0px 5px 0px 5px',
    width: '300px'
  },
  buttonGroupLeft: {
    textAlign: 'left',
    float: 'left'
  },
  buttonRight: {
    textAlign: 'right'
  },
  buttonGroupRight: {
    textAlign: 'right'
  },
  textInput100: {
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  }
});

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account
    }),
    dispatch => ({
      actions: bindActionCreators(
        {
          getAccounts,
          getGochiEventSetting
        },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openAdd', 'updateOpenAdd', false),
  withState('openAddConfirm', 'updateOpenAddConfirm', false),
  withState('openDrawer', 'updateOpenDrawer', false),
  withState('openTable', 'updateOpenTable', false),
  withState('openProgress', 'updateOpenProgress', false),
  withState('loading', 'updateLoading', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('gochiEventId', 'setGochiEventId', null),
  withState('gochiEventName', 'setGochiEventName', ''),
  withState('page', 'setPage', 1),
  withState('rowsPerPage', 'setRowsPerPage', 100),
  withState('identityStatus', 'setIdentityStatus', ''),
  withState('showHide', 'setShowHide', ''),
  withState('menuChildType', 'setMenuChildType', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleRedirect: props => path => {
      props.history.push(path);
    },
    handleChangePage: props => (event, page, limit) => {
      const table = document.getElementById('scrollTableId');
      if (table !== null) {
        table.scrollTop = 0;
      }
      props.setPage(page);

      const rowsPerPage = document.getElementById('rowsPerPage');
      let limitNum = 100;
      if (rowsPerPage) {
        limitNum = rowsPerPage.textContent;
      }

      const searchKey = {
        gochiEventId: props.gochiEventId,
        gochiEventName: props.gochiEventName,
        page,
        limit: limit || limitNum
      };

      props.updateLoading(true);
      props.updateOpenProgress(true);
      props.actions
        .getGochiEventSetting(searchKey)
        .then(data => {
          if (data.error === true && data.payload.status === 403) {
            sendLogin(props);
          }
          props.updateOpenProgress(false);
          props.setResultMessage('');
          props.updateLoading(false);
          props.updateOpenTable(true);
        })
        .catch(() => {
          props.updateOpenProgress(false);
          props.setResultMessage('');
          props.updateLoading(false);
        });
    }
  }),
  lifecycle({
    componentDidMount() {
      isAuthenticated(this.props);
      this.props.actions.getAccounts({ cognitoUserId: '' }).then(async data => {
        this.props.checkSession(data);
      });
    }
  })
);

const Main = enhance(
  ({
    classes,
    handleChangePage,
    account,
    openDrawer,
    updateOpenDrawer,
    openTable,
    updateOpenTable,
    openAdd,
    updateOpenAdd,
    openAddConfirm,
    updateOpenAddConfirm,
    loading,
    updateLoading,
    openProgress,
    updateOpenProgress,
    gochiEventId,
    setGochiEventId,
    gochiEventName,
    setGochiEventName,
    resultMessage,
    setResultMessage,
    page
  }) => {
    const isSecretariat =
      typeof account !== 'undefined' && account.role === 'SECRETARIAT';
    const roles = {
      isSecretariat
    };

    const progress = {
      loading,
      updateLoading,
      openProgress,
      updateOpenProgress,
      setResultMessage
    };

    const rowsPerPage = document.getElementById('rowsPerPage');
    let limitNum = 100;
    if (rowsPerPage) {
      limitNum = rowsPerPage.textContent;
    }

    const searchKey = {
      gochiEventId,
      gochiEventName,
      page,
      limit: limitNum
    };
    const searchAction = async () => {
      updateOpenTable(true);
      handleChangePage(null, 1, limitNum);
    };

    const addAction = async () => {
      updateOpenAdd(true);
    };

    const progressOkAction = () => {
      updateOpenProgress(false);
    };

    return (
      <div className={classes.root}>
        <Header
          onMenuClick={() => {
            updateOpenDrawer(true);
          }}
        />
        <Drawer
          open={openDrawer}
          anchor="left"
          onClose={() => {
            updateOpenDrawer(false);
          }}
        >
          <div tabIndex={0} role="button">
            <MenuDrawer account={account} />
          </div>
        </Drawer>
        <div className={classes.pageTitileDiv}>
          <Typography variant="h6" color="textSecondary">
            イベント設定画面
          </Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <div className={classes.searchForm}>
            <div>
              <TextField
                label="イベントID"
                placeholder="部分一致"
                type="search"
                className={[classes.textField, classes.textInput100]}
                margin="normal"
                value={gochiEventId}
                onChange={e => {
                  setGochiEventId(e.target.value);
                }}
              />
              <TextField
                label="イベント名"
                placeholder="部分一致"
                type="search"
                className={[classes.textField, classes.textInput100]}
                margin="normal"
                value={gochiEventName}
                onChange={e => {
                  setGochiEventName(e.target.value);
                }}
              />
            </div>
            <div className={classes.buttonGroupLeft}>
              <SearchButton onClickAction={searchAction} />
            </div>
            <div className={classes.buttonGroupRight}>
              <MenuRegisterButton
                btnText="イベント追加"
                onClickAction={addAction}
              />
            </div>
          </div>
          {openTable && (
            <GochiEventSettingTable
              searchKey={searchKey}
              roles={roles}
              progress={progress}
              page={page}
              handleChangePage={handleChangePage}
              selectedPage={page}
            />
          )}
          <div>
            <Modal open={openAdd}>
              <GochiEventSettingAdd
                openAddConfirm={openAddConfirm}
                updateOpenAdd={updateOpenAdd}
                updateOpenAddConfirm={updateOpenAddConfirm}
                searchKey={searchKey}
                roles={roles}
              />
            </Modal>
          </div>
          <ProgressDialog openProgress={openProgress} loading={loading} />
        </Paper>
        <ProgressDialog
          openProgress={openProgress}
          progressOkAction={progressOkAction}
          loading={loading}
          resultMessage={resultMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
